import {useCallback, useMemo} from 'react';

import {neverHappens} from '@shared/lib/utils/type_utils';
import {ScraperItem} from '@shared/models/parser';

import {
  useOptionalNumberQueryString,
  useOptionalStringArrayQueryString,
  useOptionalStringQueryString,
} from '@shared_web/lib/use_query_string';

import type {SearchFilters} from '@website_frontend/lib/user_data_storage';

/**
 * Filter parameter names that accept number values
 */
type NumberFilterParam =
  | 'priceMin'
  | 'priceMax'
  | 'surfaceMin'
  | 'surfaceMax'
  | 'bedroomsMin'
  | 'bedroomsMax'
  | 'roomsMin'
  | 'roomsMax'
  | 'landSurfaceMin'
  | 'landSurfaceMax'
  | 'floorNbMin'
  | 'floorNbMax'
  | 'imagesMin'
  | 'imagesMax';

/**
 * Filter parameter names that accept string values
 */
type StringFilterParam = 'energyCertificateMin' | 'energyCertificateMax';

/**
 * Filter parameter names that accept string array values
 */
type StringArrayFilterParam = 'typeLabel';

/**
 * All filter parameter names
 */
type FilterParamName = NumberFilterParam | StringFilterParam | StringArrayFilterParam;

/**
 * Get the value type for a filter parameter
 */
type FilterValueType<T extends FilterParamName> = T extends NumberFilterParam
  ? number | undefined
  : T extends StringFilterParam
    ? string | undefined
    : T extends StringArrayFilterParam
      ? string[] | undefined
      : never;

/**
 * Hook to get all filter values from query strings and a setter function
 */
export function useFilters(): {
  filters: SearchFilters;
  setFilter: <T extends FilterParamName>(paramName: T, value: FilterValueType<T>) => void;
  removeTypeLabel: (value: string) => void;
} {
  const [priceMin, setPriceMin] = useOptionalNumberQueryString('priceMin');
  const [priceMax, setPriceMax] = useOptionalNumberQueryString('priceMax');
  const [surfaceMin, setSurfaceMin] = useOptionalNumberQueryString('surfaceMin');
  const [surfaceMax, setSurfaceMax] = useOptionalNumberQueryString('surfaceMax');
  const [bedroomsMin, setBedroomsMin] = useOptionalNumberQueryString('bedroomsMin');
  const [bedroomsMax, setBedroomsMax] = useOptionalNumberQueryString('bedroomsMax');
  const [roomsMin, setRoomsMin] = useOptionalNumberQueryString('roomsMin');
  const [roomsMax, setRoomsMax] = useOptionalNumberQueryString('roomsMax');
  const [landSurfaceMin, setLandSurfaceMin] = useOptionalNumberQueryString('landSurfaceMin');
  const [landSurfaceMax, setLandSurfaceMax] = useOptionalNumberQueryString('landSurfaceMax');
  const [floorNbMin, setFloorNbMin] = useOptionalNumberQueryString('floorNbMin');
  const [floorNbMax, setFloorNbMax] = useOptionalNumberQueryString('floorNbMax');
  const [energyCertificateMin, setEnergyCertificateMin] =
    useOptionalStringQueryString('energyCertificateMin');
  const [energyCertificateMax, setEnergyCertificateMax] =
    useOptionalStringQueryString('energyCertificateMax');
  const [typeLabels, setTypeLabels] = useOptionalStringArrayQueryString('typeLabel', []);
  const [imagesMin, setImagesMin] = useOptionalNumberQueryString('imagesMin');
  const [imagesMax, setImagesMax] = useOptionalNumberQueryString('imagesMax');

  const filters = useMemo<SearchFilters>(
    () => ({
      priceMin,
      priceMax,
      surfaceMin,
      surfaceMax,
      bedroomsMin,
      bedroomsMax,
      roomsMin,
      roomsMax,
      landSurfaceMin,
      landSurfaceMax,
      floorNbMin,
      floorNbMax,
      energyCertificateMin,
      energyCertificateMax,
      typeLabel: typeLabels && typeLabels.length > 0 ? typeLabels : undefined,
      imagesMin,
      imagesMax,
    }),
    [
      priceMin,
      priceMax,
      surfaceMin,
      surfaceMax,
      bedroomsMin,
      bedroomsMax,
      roomsMin,
      roomsMax,
      landSurfaceMin,
      landSurfaceMax,
      floorNbMin,
      floorNbMax,
      energyCertificateMin,
      energyCertificateMax,
      typeLabels,
      imagesMin,
      imagesMax,
    ]
  );

  const setFilter = useCallback(
    <T extends FilterParamName>(paramName: T, value: FilterValueType<T>) => {
      if (paramName === 'priceMin') {
        setPriceMin(value as number | undefined);
      } else if (paramName === 'priceMax') {
        setPriceMax(value as number | undefined);
      } else if (paramName === 'surfaceMin') {
        setSurfaceMin(value as number | undefined);
      } else if (paramName === 'surfaceMax') {
        setSurfaceMax(value as number | undefined);
      } else if (paramName === 'bedroomsMin') {
        setBedroomsMin(value as number | undefined);
      } else if (paramName === 'bedroomsMax') {
        setBedroomsMax(value as number | undefined);
      } else if (paramName === 'roomsMin') {
        setRoomsMin(value as number | undefined);
      } else if (paramName === 'roomsMax') {
        setRoomsMax(value as number | undefined);
      } else if (paramName === 'landSurfaceMin') {
        setLandSurfaceMin(value as number | undefined);
      } else if (paramName === 'landSurfaceMax') {
        setLandSurfaceMax(value as number | undefined);
      } else if (paramName === 'floorNbMin') {
        setFloorNbMin(value as number | undefined);
      } else if (paramName === 'floorNbMax') {
        setFloorNbMax(value as number | undefined);
      } else if (paramName === 'energyCertificateMin') {
        setEnergyCertificateMin(value as string | undefined);
      } else if (paramName === 'energyCertificateMax') {
        setEnergyCertificateMax(value as string | undefined);
      } else if (paramName === 'typeLabel') {
        // biome-ignore lint/suspicious/noExplicitAny: type narrowing needed for array filter
        setTypeLabels(value as any);
      } else if (paramName === 'imagesMin') {
        setImagesMin(value as number | undefined);
      } else if (paramName === 'imagesMax') {
        setImagesMax(value as number | undefined);
      } else {
        neverHappens(paramName);
      }
    },
    [
      setPriceMin,
      setPriceMax,
      setSurfaceMin,
      setSurfaceMax,
      setBedroomsMin,
      setBedroomsMax,
      setRoomsMin,
      setRoomsMax,
      setLandSurfaceMin,
      setLandSurfaceMax,
      setFloorNbMin,
      setFloorNbMax,
      setEnergyCertificateMin,
      setEnergyCertificateMax,
      setTypeLabels,
      setImagesMin,
      setImagesMax,
    ]
  );

  const removeTypeLabel = useCallback(
    (value: string) => {
      setTypeLabels(prev => (prev ?? []).filter(v => v !== value));
    },
    [setTypeLabels]
  );

  return {filters, setFilter, removeTypeLabel};
}

/**
 * Convert DPE rating to numeric value for comparison (A=0, B=1, ..., G=6)
 */
export function dpeToNumber(dpe: string | undefined): number | undefined {
  if (dpe === undefined) return undefined;
  const rating = dpe.toUpperCase();
  const index = ['A', 'B', 'C', 'D', 'E', 'F', 'G'].indexOf(rating);
  return index >= 0 ? index : undefined;
}

/**
 * Convert filters to URLSearchParams
 */
export function filtersToUrlParams(filters: SearchFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.priceMin !== undefined) params.set('priceMin', String(filters.priceMin));
  if (filters.priceMax !== undefined) params.set('priceMax', String(filters.priceMax));
  if (filters.surfaceMin !== undefined) params.set('surfaceMin', String(filters.surfaceMin));
  if (filters.surfaceMax !== undefined) params.set('surfaceMax', String(filters.surfaceMax));
  if (filters.bedroomsMin !== undefined) params.set('bedroomsMin', String(filters.bedroomsMin));
  if (filters.bedroomsMax !== undefined) params.set('bedroomsMax', String(filters.bedroomsMax));
  if (filters.roomsMin !== undefined) params.set('roomsMin', String(filters.roomsMin));
  if (filters.roomsMax !== undefined) params.set('roomsMax', String(filters.roomsMax));
  if (filters.landSurfaceMin !== undefined)
    params.set('landSurfaceMin', String(filters.landSurfaceMin));
  if (filters.landSurfaceMax !== undefined)
    params.set('landSurfaceMax', String(filters.landSurfaceMax));
  if (filters.floorNbMin !== undefined) params.set('floorNbMin', String(filters.floorNbMin));
  if (filters.floorNbMax !== undefined) params.set('floorNbMax', String(filters.floorNbMax));
  if (filters.energyCertificateMin !== undefined)
    params.set('energyCertificateMin', filters.energyCertificateMin);
  if (filters.energyCertificateMax !== undefined)
    params.set('energyCertificateMax', filters.energyCertificateMax);
  if (filters.typeLabel && filters.typeLabel.length > 0) {
    for (const typeLabel of filters.typeLabel) {
      params.append('typeLabel', typeLabel);
    }
  }
  if (filters.imagesMin !== undefined) params.set('imagesMin', String(filters.imagesMin));
  if (filters.imagesMax !== undefined) params.set('imagesMax', String(filters.imagesMax));

  return params;
}

/**
 * Check if a single item matches the filters
 */
export function itemMatchesFilters(item: ScraperItem, filters: SearchFilters): boolean {
  const itemDpeValue = dpeToNumber(item.energyCertificate);
  const minDpeValue = dpeToNumber(filters.energyCertificateMin);
  const maxDpeValue = dpeToNumber(filters.energyCertificateMax);

  return (
    (filters.priceMin === undefined ||
      (item.price !== undefined && item.price >= filters.priceMin)) &&
    (filters.priceMax === undefined ||
      (item.price !== undefined && item.price <= filters.priceMax)) &&
    (filters.surfaceMin === undefined ||
      (item.areaM2 !== undefined && item.areaM2 >= filters.surfaceMin)) &&
    (filters.surfaceMax === undefined ||
      (item.areaM2 !== undefined && item.areaM2 <= filters.surfaceMax)) &&
    (filters.bedroomsMin === undefined ||
      (item.nbBedrooms !== undefined && item.nbBedrooms >= filters.bedroomsMin)) &&
    (filters.bedroomsMax === undefined ||
      (item.nbBedrooms !== undefined && item.nbBedrooms <= filters.bedroomsMax)) &&
    (filters.roomsMin === undefined ||
      (item.nbRooms !== undefined && item.nbRooms >= filters.roomsMin)) &&
    (filters.roomsMax === undefined ||
      (item.nbRooms !== undefined && item.nbRooms <= filters.roomsMax)) &&
    (filters.landSurfaceMin === undefined ||
      (item.landSurface !== undefined && item.landSurface >= filters.landSurfaceMin)) &&
    (filters.landSurfaceMax === undefined ||
      (item.landSurface !== undefined && item.landSurface <= filters.landSurfaceMax)) &&
    (filters.floorNbMin === undefined ||
      (item.floorNb !== undefined && item.floorNb >= filters.floorNbMin)) &&
    (filters.floorNbMax === undefined ||
      (item.floorNb !== undefined && item.floorNb <= filters.floorNbMax)) &&
    (minDpeValue === undefined || (itemDpeValue !== undefined && itemDpeValue >= minDpeValue)) &&
    (maxDpeValue === undefined || (itemDpeValue !== undefined && itemDpeValue <= maxDpeValue)) &&
    (filters.typeLabel === undefined ||
      filters.typeLabel.length === 0 ||
      (item.typeLabel !== undefined && filters.typeLabel.includes(item.typeLabel))) &&
    (filters.imagesMin === undefined || (item.images?.length ?? 0) >= filters.imagesMin) &&
    (filters.imagesMax === undefined || (item.images?.length ?? 0) <= filters.imagesMax)
  );
}

/**
 * Filter items based on search filters
 */
export function applyFilters(items: ScraperItem[], filters: SearchFilters): ScraperItem[] {
  return items.filter(item => itemMatchesFilters(item, filters));
}

/**
 * Get active filter chips for display
 */
export type ActiveFilter = {
  label: string;
  paramName: string;
  value: number | string;
};

export function getActiveFilters(filters: SearchFilters): ActiveFilter[] {
  const activeFilters: ActiveFilter[] = [];

  if (filters.priceMin !== undefined) {
    activeFilters.push({
      label: `Prix min: ${filters.priceMin.toLocaleString()} €`,
      paramName: 'priceMin',
      value: filters.priceMin,
    });
  }
  if (filters.priceMax !== undefined) {
    activeFilters.push({
      label: `Prix max: ${filters.priceMax.toLocaleString()} €`,
      paramName: 'priceMax',
      value: filters.priceMax,
    });
  }
  if (filters.surfaceMin !== undefined) {
    activeFilters.push({
      label: `Surface min: ${filters.surfaceMin} m²`,
      paramName: 'surfaceMin',
      value: filters.surfaceMin,
    });
  }
  if (filters.surfaceMax !== undefined) {
    activeFilters.push({
      label: `Surface max: ${filters.surfaceMax} m²`,
      paramName: 'surfaceMax',
      value: filters.surfaceMax,
    });
  }
  if (filters.bedroomsMin !== undefined) {
    activeFilters.push({
      label: `Chambres min: ${filters.bedroomsMin}`,
      paramName: 'bedroomsMin',
      value: filters.bedroomsMin,
    });
  }
  if (filters.bedroomsMax !== undefined) {
    activeFilters.push({
      label: `Chambres max: ${filters.bedroomsMax}`,
      paramName: 'bedroomsMax',
      value: filters.bedroomsMax,
    });
  }
  if (filters.roomsMin !== undefined) {
    activeFilters.push({
      label: `Pièces min: ${filters.roomsMin}`,
      paramName: 'roomsMin',
      value: filters.roomsMin,
    });
  }
  if (filters.roomsMax !== undefined) {
    activeFilters.push({
      label: `Pièces max: ${filters.roomsMax}`,
      paramName: 'roomsMax',
      value: filters.roomsMax,
    });
  }
  if (filters.landSurfaceMin !== undefined) {
    activeFilters.push({
      label: `Terrain min: ${filters.landSurfaceMin} m²`,
      paramName: 'landSurfaceMin',
      value: filters.landSurfaceMin,
    });
  }
  if (filters.landSurfaceMax !== undefined) {
    activeFilters.push({
      label: `Terrain max: ${filters.landSurfaceMax} m²`,
      paramName: 'landSurfaceMax',
      value: filters.landSurfaceMax,
    });
  }
  if (filters.floorNbMin !== undefined) {
    activeFilters.push({
      label: `Étage min: ${filters.floorNbMin}`,
      paramName: 'floorNbMin',
      value: filters.floorNbMin,
    });
  }
  if (filters.floorNbMax !== undefined) {
    activeFilters.push({
      label: `Étage max: ${filters.floorNbMax}`,
      paramName: 'floorNbMax',
      value: filters.floorNbMax,
    });
  }
  if (filters.energyCertificateMin !== undefined) {
    activeFilters.push({
      label: `DPE min: ${filters.energyCertificateMin}`,
      paramName: 'energyCertificateMin',
      value: filters.energyCertificateMin,
    });
  }
  if (filters.energyCertificateMax !== undefined) {
    activeFilters.push({
      label: `DPE max: ${filters.energyCertificateMax}`,
      paramName: 'energyCertificateMax',
      value: filters.energyCertificateMax,
    });
  }
  // Add individual chips for each selected typeLabel
  if (filters.typeLabel) {
    for (const typeLabel of filters.typeLabel) {
      activeFilters.push({
        label: `Type: ${typeLabel}`,
        paramName: 'typeLabel',
        value: typeLabel,
      });
    }
  }

  return activeFilters;
}
